import React from "react";
import { action, computed, observable, runInAction, toJS } from "mobx";
import { observer } from "mobx-react";
import Tooltip, { TooltipSize } from "../Tooltip/Tooltip";
import ErrorTooltip from "../Tooltip/ErrorTooltip";
import TextInput, { ITextInputProps, TextInputType, TextInputSize } from "../TextInput/TextInput";
import Dropdown, { DropdownSize, IDropdownProps } from "../dropdown/Dropdown/Dropdown";
import DropdownItem from "../dropdown/DropdownItem/DropdownItem";
import RadioButtonGroup, { IRadioButtonGroupProps } from "../RadioButtonGroup/RadioButtonGroup";
import Button, { ButtonSize } from "../Button/Button";
import classNames from "classnames";
import { StricterOptionGroup, IStricterOptionGroupProps, OptionGroupSize } from "../OptionGroup/OptionGroup";
import crypto from "crypto";

export enum FormType {
  Text = "text",
  Dropdown = "dropdown",
  Radio = "radio",
  OptionGroup = "optionGroup",
}

export enum FormWidth {
  Normal = "normal",
  Wide = "wide",
}

export interface IFormDataStore {
  getById: (id: string) => string;
  setById: (id: string, value: string) => void;
}

interface IFormOption {
  id: string;
  name: string;
}

interface IFormField {
  id: string;
  type: FormType;
  props: ITextInputProps | IDropdownProps | IRadioButtonGroupProps | IStricterOptionGroupProps;
  validate: (value: string) => string | void;
  /**
   * Only for use in a dropdown field. Sets the options available from the dropdown.
   */
  options?: IFormOption[];
  /**
   * Only for use in a dropdown field. Enables showing a text field the user can type
   * in if they select "Other" from a Dropdown field.
   */
  showOtherTextInput?: boolean;
  /**
   * Only for use in a dropdown field. Sets the props for the text field shown
   * if the user selects "Other".
   */
  otherTextInputProps?: Partial<ITextInputProps>;
}

interface IFormValues {
  [id: string]: string;
}

interface IFormProps {
  fields: IFormField[];
  onComplete: (values: IFormValues) => Promise<any>;
  dataStore?: IFormDataStore;
  allowDevAutocomplete?: boolean;
  width?: FormWidth;
  buttonText?: React.ReactNode;
  showEnterKeyPrompt?: boolean;
  className?: string;
}

enum SpecialDropdownValue {
  /**
   * This dropdown value represents an "Other" value where we need to ask the user
   * for additional information pertaining to their answer
   */
  Other = "other",
}

@observer
class Form extends React.Component<IFormProps> {
  public static defaultProps: Partial<IFormProps> = {
    width: FormWidth.Normal,
    allowDevAutocomplete: false,
    buttonText: "Submit",
    showEnterKeyPrompt: false,
  };

  @observable.deep
  private dropdownValues: IFormValues = {};

  @observable.deep
  private textValues: IFormValues = {};

  @observable.deep
  private radioValues: IFormValues = {};

  @observable.deep
  private optionValues: IFormValues = {};

  @observable.ref
  private shouldShowError = false;

  @observable.ref
  private submitCount = 0;

  @observable.ref
  private isSubmitting = false;

  @observable.ref
  private externalError = "";

  constructor(props: IFormProps) {
    super(props);
  }

  public componentDidMount(): void {
    if (this.props.showEnterKeyPrompt) {
      window.document.addEventListener("keyup", this.maybeSubmit);
    }
  }

  public componentWillUnmount(): void {
    if (this.props.showEnterKeyPrompt) {
      window.document.removeEventListener("keyup", this.maybeSubmit);
    }
  }

  @action.bound
  private setValue(fieldType: FormType, fieldId: string, value: string): void {
    switch (fieldType) {
      case FormType.Text: {
        this.textValues[fieldId] = value;
        break;
      }
      case FormType.Dropdown: {
        this.dropdownValues[fieldId] = value;
        break;
      }
      case FormType.Radio: {
        this.radioValues[fieldId] = value;
        break;
      }
      case FormType.OptionGroup: {
        this.optionValues[fieldId] = value;
        break;
      }
    }
    if (this.props.dataStore) {
      this.props.dataStore.setById(fieldId, value);
    }
    if (this.shouldShowError) {
      this.shouldShowError = false;
    }
  }

  @computed
  private get values(): IFormValues {
    const values = {} as IFormValues;
    this.props.fields.forEach((field: IFormField) => {
      // If the dropdown is set to Other, defer to using the text field. This would only be applicable if you had showOtherTextInput
      // on or else the text field would not be editable/visible.
      if (
        field.type === FormType.Dropdown &&
        this.getValue(field.id, FormType.Dropdown).toLowerCase() === SpecialDropdownValue.Other
      ) {
        values[field.id] = this.getValue(field.id, FormType.Text) || this.getValue(field.id, FormType.Dropdown);
      } else {
        values[field.id] = this.getValue(field.id, field.type);
      }
    });
    return values;
  }

  private getValue(fieldId: string, fieldType?: FormType): string {
    if (this.props.dataStore) {
      return this.props.dataStore.getById(fieldId);
    }
    return computed(() => {
      switch (fieldType) {
        case FormType.Text: {
          return this.textValues[fieldId] || "";
        }
        case FormType.Dropdown: {
          return this.dropdownValues[fieldId] || "";
        }
        case FormType.Radio: {
          return this.radioValues[fieldId] || "";
        }
        case FormType.OptionGroup: {
          return this.optionValues[fieldId] || "";
        }
        default: {
          // This is a used when the Dropdown is set to Other
          // And will prefer returning the text value over "Other" as an option
          return (
            this.textValues[fieldId] ||
            this.dropdownValues[fieldId] ||
            this.radioValues[fieldId] ||
            this.optionValues[fieldId] ||
            ""
          );
        }
      }
    }).get();
  }

  @computed
  private get isValid(): boolean {
    let isValid = true;

    for (const field of this.props.fields) {
      const value = this.getValue(field.id);
      const errorMessage = field.validate(value);
      isValid = isValid && !errorMessage;
    }

    return isValid;
  }

  @computed
  private get errorField(): IFormField | undefined {
    let result;

    for (let i = 0; i < this.props.fields.length; i++) {
      const field = this.props.fields[i];
      const value = this.getValue(field.id);
      const errorMessage = field.validate(value);
      if (errorMessage) {
        result = field;
        break;
      }
    }

    return result;
  }

  public render(): JSX.Element {
    const onFormKeyUp = !this.props.showEnterKeyPrompt ? this.maybeSubmit : undefined;
    return (
      <div
        className={classNames("form form--kl", `form--${this.props.width}`, this.props.className)}
        onKeyUp={onFormKeyUp}
      >
        {this.fields}
        <div className="form__button-spacer" />
        <div
          className={classNames("form__button-with-prompt", {
            "form__button-with-prompt--with-enter-key": this.props.showEnterKeyPrompt,
            "form__button-with-prompt--single-action": !this.props.showEnterKeyPrompt,
          })}
        >
          <ErrorTooltip
            renderTooltip={!!this.externalError}
            content={this.externalError || ""}
            size={TooltipSize.Large}
          >
            {/* We do input validation on mouse enter the Submit button. However, onMouseEnter event
             is not fired when the button is disabled. Therefore, we had to wrap the button
             inside a div as a workaround*/}
            <div className="form__button-wrapper" onMouseEnter={this.onMouseEnter}>
              <Button
                className="form__button"
                onClick={this.onSubmit}
                size={ButtonSize.Large}
                disabled={!this.isValid}
                data-synthetics-selector="form-button"
              >
                {this.props.buttonText}
              </Button>
            </div>
          </ErrorTooltip>
          {this.props.showEnterKeyPrompt && <div className="form__enter-key">or press ‘Enter’ key</div>}
        </div>
      </div>
    );
  }

  @computed
  private get fields(): JSX.Element {
    return (
      <>
        {this.props.fields.map((field: IFormField, index: number) => (
          <div key={index}>
            {this.renderField(field)}
            {index !== this.props.fields.length - 1 && <div className="form__spacer" />}
          </div>
        ))}
      </>
    );
  }

  private renderField(field: IFormField): React.ReactNode {
    const value = this.getValue(field.id);
    const shouldShowError = this.shouldShowError && this.errorField && this.errorField.id == field.id;
    const errorMessage = field.validate(value) || "";

    if (field.type === FormType.Text) {
      return (
        <Tooltip isError content={errorMessage} size={TooltipSize.Large} show={shouldShowError}>
          {this.renderTextInput(field)}
        </Tooltip>
      );
    } else if (field.type === FormType.Dropdown) {
      const dropdownValue = this.getDropdownOption(field, this.getValue(field.id, FormType.Dropdown));
      const showOtherTextInput = dropdownValue.toLowerCase() === SpecialDropdownValue.Other && field.showOtherTextInput;
      return (
        <>
          <Tooltip
            isError
            content={errorMessage}
            size={TooltipSize.Large}
            show={!showOtherTextInput && shouldShowError}
          >
            {this.renderDropdown(field)}
          </Tooltip>
          {showOtherTextInput && (
            <Tooltip isError content={errorMessage} size={TooltipSize.Large} show={shouldShowError}>
              {this.renderDropdownOtherTextInput(field)}
            </Tooltip>
          )}
        </>
      );
    } else if (field.type === FormType.Radio) {
      return (
        <Tooltip isError content={errorMessage} size={TooltipSize.Large} show={shouldShowError}>
          {this.renderRadioButtonGroup(field)}
        </Tooltip>
      );
    } else if (field.type === FormType.OptionGroup) {
      return (
        <Tooltip isError content={errorMessage} size={TooltipSize.Large} show={shouldShowError}>
          {this.renderOptionGroup(field)}
        </Tooltip>
      );
    }
  }

  private renderTextInput(field: IFormField): JSX.Element {
    return (
      <TextInput
        size={TextInputSize.Large}
        {...(field.props as ITextInputProps)}
        value={this.getValue(field.id)}
        onChange={(value): void => {
          this.setValue(field.type, field.id, value);
        }}
      />
    );
  }

  private renderDropdown(field: IFormField): JSX.Element {
    const dropdownValue = this.getDropdownOption(field, this.getValue(field.id, FormType.Dropdown));
    return (
      <Dropdown size={DropdownSize.Large} {...(field.props as IDropdownProps)} name={dropdownValue}>
        {(field.options || []).map((option: IFormOption, index: number) => (
          <DropdownItem
            key={index}
            name={option.name}
            selected={dropdownValue === option.name}
            onSelect={(): void => {
              this.setValue(FormType.Dropdown, field.id, option.id);
            }}
          />
        ))}
      </Dropdown>
    );
  }

  private renderDropdownOtherTextInput(field: IFormField): JSX.Element {
    return (
      <TextInput
        {...(field.props as ITextInputProps)}
        labelHidden
        label="Other"
        size={TextInputSize.Large}
        {...field.otherTextInputProps}
        className="form__other-for-dropdown"
        value={this.getValue(field.id, FormType.Text)}
        onChange={(value): void => {
          this.setValue(FormType.Text, field.id, value);
        }}
      />
    );
  }

  private renderRadioButtonGroup(field: IFormField): JSX.Element {
    return (
      <RadioButtonGroup
        {...(field.props as IRadioButtonGroupProps)}
        selectedId={this.getValue(field.id)}
        onClickHandler={(value): void => {
          this.setValue(field.type, field.id, value);
        }}
      />
    );
  }

  private renderOptionGroup(field: IFormField): JSX.Element {
    return (
      <StricterOptionGroup
        {...(field.props as IStricterOptionGroupProps)}
        size={OptionGroupSize.Large}
        selectedValue={this.getValue(field.id, FormType.OptionGroup)}
        onClickHandler={(value): void => {
          this.setValue(field.type, field.id, value);
        }}
      />
    );
  }

  private getDropdownOption = (field: IFormField, optionId: string): string => {
    let optionName = "";

    for (const option of field.options || []) {
      if (option.id == optionId) {
        optionName = option.name;
        break;
      }
    }
    return optionName;
  };

  private isSubmitKeyEvent = (event: React.KeyboardEvent | KeyboardEvent): boolean => {
    const nodeName = (event.target as HTMLElement).nodeName;
    return event.key == "Enter" && nodeName.toLowerCase() != "textarea";
  };

  private maybeSubmit = (event: React.KeyboardEvent | KeyboardEvent): void => {
    if (this.isSubmitKeyEvent(event) && !this.isSubmitting) {
      if (this.props.allowDevAutocomplete) {
        for (let i = 0; i < this.props.fields.length; i++) {
          const field = this.props.fields[i];
          if (field.type === FormType.Text) {
            if (!this.getValue(field.id, field.type)) {
              if ((field.props as ITextInputProps).type === TextInputType.Email) {
                /*
                  This is to make signups easier but also this makes sense everywhere. If this is the email that's
                  used in login, that's a pretty glaring problem if we're hardcoding the password or something
                */
                this.setValue(field.type, field.id, getRandomEmail((field.props as ITextInputProps).placeholder || ""));
              } else {
                this.setValue(field.type, field.id, (field.props as ITextInputProps).placeholder || "");
              }
            }
          } else if (field.type === FormType.OptionGroup) {
            if (!this.getValue(field.id, field.type)) {
              this.setValue(field.type, field.id, (field.props as IStricterOptionGroupProps).options[0].value);
            }
          } else {
            if (!this.getValue(field.id, field.type) && field.options) {
              this.setValue(field.type, field.id, field.options[0].id);
            }
          }
        }
      }
      this.onSubmit();
    }
  };

  @action.bound
  private async onSubmit(): Promise<void> {
    if (this.isSubmitting) {
      // Early exit so as not to allow multiple submissions at any one time
      return;
    } else if (this.isValid) {
      try {
        this.setIsSubmitting(true);
        this.externalError = "";
        await this.props.onComplete(toJS(this.values));
      } catch (error) {
        this.externalError = error.message;
        throw error;
      } finally {
        this.setIsSubmitting(false);
      }
    } else {
      this.showError();
      // We need this here to reject the onSubmit function
      throw new Error();
    }
  }

  @action.bound
  private setIsSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
  }

  @action.bound
  private async onMouseEnter(): Promise<void> {
    if (!this.isValid) {
      this.showError();
    }
  }

  @action.bound
  private showError(): void {
    const submitCount = ++this.submitCount;
    this.shouldShowError = true;
    setTimeout(() => {
      runInAction(() => {
        if (this.submitCount === submitCount) {
          this.shouldShowError = false;
        }
      });
    }, 3500);
  }
}

const getRandomEmail = (baseEmail: string): string => {
  return baseEmail.replace(
    /@.+$/,
    `${crypto.randomBytes(16).toString("base64").replace(/\+/g, "_").replace(/=/g, ".")}@testing.com`,
  );
};

export { IFormField, IFormValues };
export default Form;
